import type { LineItem } from '../types';

// Phase 25: pre-compute sanity check. Surfaces the high-level fleet shape
// (count, vocation mix, fuel-type mix, avg acquisition) so the user can
// confirm they entered what they intended before kicking off a fleet
// computation. Intentionally compact — one or two rows of text, no chrome.

function fmt$(v: number): string {
  if (!isFinite(v) || v <= 0) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export default function FleetHealthCheck({ lineItems }: { lineItems: LineItem[] }) {
  if (lineItems.length === 0) return null;

  const totalVehicles = lineItems.reduce((acc, li) => acc + (li.quantity ?? 0), 0);
  if (totalVehicles === 0) return null;

  // Fuel-type counts (grouped by fuel_type)
  const fuelCounts: Record<string, number> = {};
  // Vocation counts
  const vocationCounts: Record<string, number> = {};
  let totalAcquisition = 0;
  for (const li of lineItems) {
    const qty = li.quantity ?? 0;
    if (qty <= 0) continue;
    const fuel = li.vehicle.operating_costs.fuel_type;
    fuelCounts[fuel] = (fuelCounts[fuel] ?? 0) + qty;
    const voc = li.vehicle.vocation_profile ?? 'unset';
    vocationCounts[voc] = (vocationCounts[voc] ?? 0) + qty;
    const price = li.vehicle.depreciation?.purchase_price ?? 0;
    totalAcquisition += qty * price;
  }
  const avgAcquisition = totalVehicles > 0 ? totalAcquisition / totalVehicles : 0;

  const fuelMix = Object.entries(fuelCounts)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
  const vocationMix = Object.entries(vocationCounts)
    .map(([k, v]) => (k === 'unset' ? `unset: ${v}` : `${k}: ${v}`))
    .join(', ');

  return (
    <div
      data-testid="fleet-health-check"
      className="cnc-panel rounded-lg px-4 py-3 mb-3"
      style={{ background: 'var(--panel2)', border: '1px solid var(--border)' }}
    >
      <div
        className="text-xs font-semibold uppercase tracking-widest mb-2"
        style={{ color: 'var(--white-dim, #94a3b8)' }}
      >
        Fleet Summary
      </div>
      <div
        className="text-sm tco-num"
        style={{
          color: 'var(--tco-ink, #e5e7eb)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 14px',
          alignItems: 'baseline',
          lineHeight: 1.6,
        }}
      >
        <span>
          <strong style={{ fontWeight: 600 }}>{totalVehicles}</strong> vehicle{totalVehicles === 1 ? '' : 's'}
        </span>
        <span style={{ color: 'var(--white-dim, #6b7280)' }}>·</span>
        <span>
          <span style={{ color: 'var(--white-dim, #94a3b8)' }}>Vocations: </span>
          {vocationMix || '—'}
        </span>
        <span style={{ color: 'var(--white-dim, #6b7280)' }}>·</span>
        <span>
          <span style={{ color: 'var(--white-dim, #94a3b8)' }}>Fuel: </span>
          {fuelMix || '—'}
        </span>
      </div>
      <div
        className="text-sm tco-num"
        style={{
          color: 'var(--tco-ink, #e5e7eb)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 14px',
          alignItems: 'baseline',
          marginTop: 4,
        }}
      >
        <span>
          <span style={{ color: 'var(--white-dim, #94a3b8)' }}>Avg purchase price: </span>
          <strong style={{ fontWeight: 600 }}>{fmt$(avgAcquisition)}</strong>
        </span>
        <span style={{ color: 'var(--white-dim, #6b7280)' }}>·</span>
        <span>
          <span style={{ color: 'var(--white-dim, #94a3b8)' }}>Total acquisition: </span>
          <strong style={{ fontWeight: 600 }}>{fmt$(totalAcquisition)}</strong>
        </span>
      </div>
    </div>
  );
}
